import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

// Context
import { AuthProvider } from './contexts/AuthContext';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LoginPage from './features/auth/LoginPage';
import SignupPage from './features/auth/SignupPage';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import ResetPasswordPage from './features/auth/ResetPasswordPage';
import ClientDashboard from './features/dashboard/ClientDashboard';
import ContractsPage from './features/contracts/ContractsPage';
import ContractDetailsPage from './features/contracts/ContractDetailsPage';
import CreateContractPage from './features/contracts/CreateContractPage';
import OTPVerification from './components/OTPVerification';
import ContractAcknowledgmentPage from './features/contracts/components/ContractAcknowledgmentPage';
import PaymentsPage from './features/payments/PaymentsPage';
import PaymentPage from './features/payments/PaymentPage';
import DocumentsPage from './features/documents/DocumentsPage';
import SettingsPage from './features/settings/SettingsPage';
import ProfilePage from './features/profile/ProfilePage';
import NotificationsPage from './features/notifications/NotificationsPage';
import FinanceManagementPage from './features/finance/FinanceManagementPage';

// RTL Cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// RTL Theme
const theme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: 'Tajawal, Arial, sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'Tajawal, Arial, sans-serif',
        },
      },
    },
  },
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Set document direction and language
  useEffect(() => {
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = 'ar';
    document.title = 'ليكس باي - LexPay';
  }, []);

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={theme}>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <Toaster position="top-left" />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                
                {/* Protected routes */}
                <Route element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }>
                  <Route path="/" element={<ClientDashboard />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/contracts" element={<ContractsPage />} />
                  <Route path="/contracts/create" element={<CreateContractPage />} />
                  <Route path="/contracts/:contractId" element={<ContractDetailsPage />} />
                  <Route path="/contracts/:contractId/acknowledge" element={<ContractAcknowledgmentPage />} />
                  <Route path="/contracts/:contractId/verify-otp" element={<OTPVerification />} />
                  <Route path="/payments" element={<PaymentsPage />} />
                  <Route path="/payments/:contractId/:paymentId" element={<PaymentPage />} />
                  <Route path="/documents" element={<DocumentsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                    <Route 
                    path="/finance" 
                    element={
                      <ProtectedRoute allowedRoles={['lawyer']}>
                        <FinanceManagementPage />
                      </ProtectedRoute>
                    } 
                  />
                </Route>
                
                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <ReactQueryDevtools initialIsOpen={false} />
            </AuthProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
